/**
 * Request-timeout helpers, deliberately kept free of any import that reaches
 * outside the module.
 *
 * These live apart from `fetchApi` because that module pulls in the logger,
 * which cannot initialise under jsdom: a test that mocks `fetchApi` to avoid
 * the network would otherwise have to mock these pure functions too, and an
 * auto-mocked `timeoutInit` answers `undefined` - something the real one can
 * never return.
 */

/**
 * Copy for a request this client gave up on. The engine's own wording is not
 * fit to show anyone - WebKit says "Fetch is aborted", Blink "signal timed
 * out" - and an abort is the one failure the user can usefully act on.
 */
export const FETCH_TIMEOUT_MESSAGE =
    "This is taking longer than usual. Please try again in a moment.";

/**
 * `AbortSignal.timeout` as a ready-made `RequestInit`, guarded for browsers
 * without it. `fetchApi`'s default is deliberately short; a caller fronting a
 * slow upstream passes its own rather than inheriting it.
 */
export function timeoutInit(ms: number): RequestInit {
    return typeof AbortSignal?.timeout === "function"
        ? { signal: AbortSignal.timeout(ms) }
        : {};
}

/**
 * Whether a rejection is this client giving up rather than an answer from the
 * server. `name` is the only portable signal: the message differs per engine,
 * so nothing may match on it. `AbortSignal.timeout` rejects with
 * `TimeoutError`, an explicit `controller.abort()` with `AbortError`; both mean
 * the same thing to a user.
 */
export function isAbortError(e: unknown): boolean {
    if (typeof e !== "object" || e === null) return false;
    const { name } = e as { name?: unknown };
    return name === "AbortError" || name === "TimeoutError";
}
